import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaChartLine, 
  FaRocket, 
  FaFileAlt, 
  FaDownload, 
  FaArrowUp, 
  FaArrowDown, 
  FaStar, 
  FaExternalLinkAlt, 
  FaShieldAlt, 
  FaExclamationTriangle, 
  FaSync,
  FaCalendarAlt,
  FaPortrait,
  FaPercentage,
  FaRupeeSign
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../Header';
import Footer from '../Footer';

const PortfolioResearchDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [sortConfig, setSortConfig] = useState({ key: 'return', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');

  // API Configuration
  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1YfIaeY8Ny0LKfhjhdFgHu6btTzZN64WE96Zyxk-z50o/values/ALL%20MM%20WEATHER%20PORTFOLIO?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      console.log('Fetching portfolio data from:', API_URL);
      
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Portfolio API Response received');
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching portfolio data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Process the data to extract portfolio, IPO, and research reports
  const processData = () => {
    if (!data || !data.values) {
      console.log('No data available for processing');
      return { portfolio: [], ipos: [], researchReports: [], disclosures: [] };
    }

    const values = data.values;
    console.log('Total rows received:', values.length);
    
    let portfolio = [];
    let ipos = [];
    let researchReports = [];
    let disclosures = [];
    
    let portfolioHeadersFound = false;
    let ipoHeadersFound = false;
    
    // Process all rows
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      // Check for portfolio headers
      if (row.includes('S.NO') && row.includes('TICKER') && row.includes('STOCK NAME') && row.includes('Reco Price') && row.includes('CMP')) {
        portfolioHeadersFound = true;
        console.log('Found portfolio headers at row:', i);
        
        // Process portfolio rows
        for (let j = i + 1; j < values.length; j++) {
          const portfolioRow = values[j];
          if (!portfolioRow || portfolioRow.length === 0) continue;
          
          // Stop if we find IPO headers or empty rows
          if (portfolioRow.includes('S.NO') && portfolioRow.includes('IPO NAME')) {
            break;
          }
          
          // Skip "COMING SOON" rows
          const firstNonEmpty = portfolioRow.find(cell => cell && cell.toString().trim() !== '');
          if (firstNonEmpty && firstNonEmpty.toString().includes('COMING SOON')) {
            continue;
          }
          
          // Find S.NO in the row
          const snoIndex = portfolioRow.findIndex(cell => {
            const num = parseInt(cell);
            return !isNaN(num) && num > 0;
          });
          
          if (snoIndex === -1) continue;
          
          const sno = parseInt(portfolioRow[snoIndex]);
          
          // Extract portfolio data based on header positions
          if (snoIndex + 3 < portfolioRow.length) {
            const ticker = portfolioRow[snoIndex + 1] || '';
            const stockName = portfolioRow[snoIndex + 2] || '';
            
            // Find numeric values for recoPrice, cmp, returnVal
            let recoPrice = 0;
            let cmp = 0;
            let returnVal = 0;
            let recoDate = '';
            
            // Search for numeric values and date in the row
            for (let k = snoIndex + 3; k < portfolioRow.length; k++) {
              const cell = portfolioRow[k];
              if (cell && cell.toString().trim() !== '') {
                // Check for date (has slashes)
                if (cell.toString().includes('/')) {
                  recoDate = cell.toString();
                }
                // Check for numeric values
                const num = parseFloat(cell.toString().replace(/,/g, ''));
                if (!isNaN(num)) {
                  if (recoPrice === 0) {
                    recoPrice = num;
                  } else if (cmp === 0) {
                    cmp = num;
                  } else if (returnVal === 0) {
                    returnVal = num;
                  }
                }
              }
            }
            
            if (ticker && stockName && ticker !== '' && stockName !== '') {
              portfolio.push({
                id: sno,
                ticker: ticker,
                stockName: stockName,
                recoPrice: recoPrice,
                cmp: cmp,
                return: returnVal,
                recoDate: recoDate,
                absoluteReturn: (cmp - recoPrice),
                percentageReturn: returnVal,
                isPositive: returnVal > 0
              });
            }
          }
        }
      }
      
      // Check for IPO headers
      if (row.includes('S.NO') && row.includes('IPO NAME') && row.includes('TICKER') && row.includes('IPO PRICE')) {
        ipoHeadersFound = true;
        console.log('Found IPO headers at row:', i);
        
        // Process IPO rows
        for (let j = i + 1; j < values.length; j++) {
          const ipoRow = values[j];
          if (!ipoRow || ipoRow.length === 0) continue;
          
          // Skip empty or incomplete rows
          const hasContent = ipoRow.some(cell => cell && cell.toString().trim() !== '');
          if (!hasContent) continue;
          
          // Find S.NO in the row
          const snoIndex = ipoRow.findIndex(cell => {
            const num = parseInt(cell);
            return !isNaN(num) && num > 0;
          });
          
          if (snoIndex === -1) continue;
          
          const sno = parseInt(ipoRow[snoIndex]);
          
          // Extract IPO data
          if (snoIndex + 4 < ipoRow.length) {
            const name = ipoRow[snoIndex + 1] || '';
            const ticker = ipoRow[snoIndex + 2] || '';
            const date = ipoRow[snoIndex + 3] || '';
            const priceRange = ipoRow[snoIndex + 4] || '';
            const currentPrice = ipoRow[snoIndex + 5] || '';
            const rating = ipoRow[snoIndex + 6] || '';
            
            // Only add if we have a name
            if (name && name.trim() !== '') {
              ipos.push({
                id: sno,
                name: name,
                ticker: ticker,
                date: date,
                priceRange: priceRange,
                currentPrice: currentPrice,
                rating: rating
              });
            }
          }
        }
      }
      
      // Check for research reports (look for Google Drive links)
      if (row.length >= 5 && row[4] && row[4].includes('drive.google.com')) {
        const companyName = row[0] || '';
        const reportLink = row[4] || '';
        
        if (companyName && reportLink) {
          researchReports.push({
            companyName: companyName,
            reportLink: reportLink,
            reportDate: new Date().toLocaleDateString() // You might want to extract date from report
          });
        }
      }
      
      // Process disclosures
      if (row.length > 0 && row[0]) {
        const text = row[0].toString();
        if (text.includes('SEBI REGISTERED')) {
          disclosures.push({ type: 'registration', text: text });
        } else if (text.includes('RISK DISCLOSURE')) {
          disclosures.push({ type: 'risk', text: text });
        } else if (text.includes('DISCLAIMER')) {
          disclosures.push({ type: 'disclaimer', text: text });
        } else if (text.includes('INVESTOR CAUTION')) {
          disclosures.push({ type: 'caution', text: text });
        }
      }
    }
    
    console.log('Processed data:', {
      portfolio: portfolio.length,
      ipos: ipos.length,
      researchReports: researchReports.length,
      disclosures: disclosures.length
    });
    
    return { portfolio, ipos, researchReports, disclosures };
  };

  const { portfolio, ipos, researchReports, disclosures } = processData();

  // Filter and sort portfolio
  const filteredAndSortedPortfolio = useMemo(() => {
    let filtered = [...portfolio];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.ticker.toLowerCase().includes(term) ||
        stock.stockName.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [portfolio, searchTerm, sortConfig]);

  // Calculate portfolio statistics
  const portfolioStats = useMemo(() => {
    if (portfolio.length === 0) return null;
    
    const totalInvested = portfolio.reduce((sum, stock) => sum + stock.recoPrice, 0);
    const currentValue = portfolio.reduce((sum, stock) => sum + stock.cmp, 0);
    const totalReturn = portfolio.reduce((sum, stock) => sum + stock.absoluteReturn, 0);
    const totalReturnPercentage = ((currentValue - totalInvested) / totalInvested) * 100;
    
    const positiveStocks = portfolio.filter(s => s.return > 0).length;
    const negativeStocks = portfolio.filter(s => s.return < 0).length;
    const neutralStocks = portfolio.filter(s => s.return === 0).length;
    
    const avgReturn = portfolio.reduce((sum, stock) => sum + stock.return, 0) / portfolio.length;
    
    return {
      totalStocks: portfolio.length,
      totalInvested,
      currentValue,
      totalReturn,
      totalReturnPercentage,
      positiveStocks,
      negativeStocks,
      neutralStocks,
      avgReturn
    };
  }, [portfolio]);

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === '') return 'N/A';
    return dateString;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio data...</p>
          <p className="text-sm text-gray-500 mt-2">Fetching data from Google Sheets</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Portfolio</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchPortfolioData}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <Header />
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
                <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Maestroo Portfolio Dashboard</h1>
                <p className="text-blue-100">Comprehensive portfolio analysis and research reports</p>
                <p className="text-blue-200 text-sm mt-1">SEBI Registered Research Analyst (INH000021757)</p>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaShieldAlt className="text-yellow-300" />
                    <span className="font-semibold">SEBI INH000021757</span>
                </div>
                <button
                    onClick={fetchPortfolioData}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                    <FaSync className="text-sm" />
                    Refresh Data
                </button>
                </div>
            </div>
            </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 py-16">
            {/* Portfolio Stats */}
            {activeTab === 'portfolio' && portfolioStats && (
            <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                >
                    <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Portfolio Value</p>
                        <p className="text-2xl font-bold text-gray-900">
                        ₹{portfolioStats.currentValue.toLocaleString('en-IN')}
                        </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FaRupeeSign className="text-blue-600 text-xl" />
                    </div>
                    </div>
                    <div className="mt-4">
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${portfolioStats.totalReturnPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {portfolioStats.totalReturnPercentage >= 0 ? '+' : ''}{portfolioStats.totalReturnPercentage.toFixed(2)}%
                        </span>
                        <span className="text-gray-500 text-sm">
                        (₹{Math.abs(portfolioStats.totalReturn).toFixed(2)})
                        </span>
                    </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                >
                    <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Total Stocks</p>
                        <p className="text-2xl font-bold text-gray-900">{portfolioStats.totalStocks}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <FaChartLine className="text-green-600 text-xl" />
                    </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {portfolioStats.positiveStocks} Gainers
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                        {portfolioStats.negativeStocks} Losers
                    </span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                >
                    <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Avg Return</p>
                        <p className={`text-2xl font-bold ${portfolioStats.avgReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {portfolioStats.avgReturn >= 0 ? '+' : ''}{portfolioStats.avgReturn.toFixed(2)}%
                        </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <FaPercentage className="text-orange-600 text-xl" />
                    </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                    Per stock average
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                >
                    <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">Total Invested</p>
                        <p className="text-2xl font-bold text-gray-900">
                        ₹{portfolioStats.totalInvested.toLocaleString('en-IN')}
                        </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FaPortrait className="text-purple-600 text-xl" />
                    </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                    Initial investment amount
                    </div>
                </motion.div>
                </div>
            </div>
            )}

            {/* Search and Sort Controls */}
            <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                <div className="relative">
                    <FaFileAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                    type="text"
                    placeholder="Search stocks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                    />
                </div>
                </div>
                <div className="flex gap-2">
                <button
                    onClick={() => handleSort('return')}
                    className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                    sortConfig.key === 'return' 
                        ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                    Sort by Return
                    {sortConfig.key === 'return' && (
                    sortConfig.direction === 'desc' ? <FaArrowDown /> : <FaArrowUp />
                    )}
                </button>
                <button
                    onClick={() => handleSort('stockName')}
                    className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                    sortConfig.key === 'stockName' 
                        ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                    Sort by Name
                </button>
                </div>
            </div>
            </div>

            {/* Portfolio Tab Content */}
            {activeTab === 'portfolio' && (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Investment Portfolio</h2>
                <p className="text-gray-600">Track performance of your stock investments with real-time data</p>
                </div>

                {filteredAndSortedPortfolio.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
                    <FaChartLine className="text-gray-400 text-4xl mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Portfolio Data Available</h3>
                    <p className="text-gray-500">Check back soon for updated portfolio information</p>
                </div>
                ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredAndSortedPortfolio.map((stock, index) => (
                    <motion.div
                        key={stock.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ y: -5 }}
                        className="bg-white rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 transition-all duration-300 overflow-hidden"
                    >
                        <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                            <h3 className="text-lg font-bold text-gray-900">{stock.stockName}</h3>
                            <p className="text-sm text-blue-600">{stock.ticker}</p>
                            {stock.recoDate && (
                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <FaCalendarAlt />
                                Recommended: {formatDate(stock.recoDate)}
                                </p>
                            )}
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            stock.isPositive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                            {stock.isPositive ? '+' : ''}{stock.return}%
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Reco Price</p>
                            <p className="text-lg font-bold text-gray-900">₹{stock.recoPrice}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Current Price</p>
                            <p className="text-lg font-bold text-gray-900">₹{stock.cmp}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Absolute Return</p>
                            <p className={`text-lg font-bold ${stock.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                {stock.isPositive ? '+' : ''}₹{Math.abs(stock.absoluteReturn).toFixed(2)}
                            </p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Return %</p>
                            <p className={`text-lg font-bold ${stock.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                {stock.isPositive ? '+' : ''}{stock.return}%
                            </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                            {stock.isPositive ? (
                                <FaArrowUp className="text-green-500 mr-2" />
                            ) : (
                                <FaArrowDown className="text-red-500 mr-2" />
                            )}
                            <span className={`font-medium ${stock.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                {stock.isPositive ? 'Profitable' : 'Underwater'}
                            </span>
                            </div>
                            <div className="text-sm text-gray-500">
                            ID: {stock.id}
                            </div>
                        </div>
                        </div>
                    </motion.div>
                    ))}
                </div>
                )}
            </motion.div>
            )}

            

            {/* Disclosures Section */}
            {disclosures.length > 0 && (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-12"
            >
                <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <FaShieldAlt className="text-blue-500" />
                    Regulatory Disclosures
                </h3>
                
                <div className="space-y-6">
                    {disclosures.map((disclosure, index) => {
                    let title = '';
                    let icon = null;
                    let color = 'text-gray-700';
                    
                    if (disclosure.type === 'registration') {
                        title = 'SEBI Registration';
                        icon = <FaShieldAlt className="text-blue-500" />;
                        color = 'text-blue-700';
                    } else if (disclosure.type === 'risk') {
                        title = 'Risk Disclosure';
                        icon = <FaExclamationTriangle className="text-amber-500" />;
                        color = 'text-amber-700';
                    } else if (disclosure.type === 'disclaimer') {
                        title = 'Disclaimer';
                        icon = <FaFileAlt className="text-purple-500" />;
                        color = 'text-purple-700';
                    } else if (disclosure.type === 'caution') {
                        title = 'Investor Caution';
                        icon = <FaExclamationTriangle className="text-red-500" />;
                        color = 'text-red-700';
                    }
                    
                    return (
                        <div key={index} className={`${color} bg-white/50 p-5 rounded-xl border border-gray-100`}>
                        <div className="flex items-start gap-3">
                            <div className="mt-1">{icon}</div>
                            <div>
                            <h4 className="font-bold mb-2">{title}</h4>
                            <p className="text-sm leading-relaxed opacity-90 whitespace-pre-line">
                                {disclosure.text.replace(/\r\n/g, '\n')}
                            </p>
                            </div>
                        </div>
                        </div>
                    );
                    })}
                </div>
                </div>
            </motion.div>
            )}

            {/* Footer */}
            <div className="mt-8 text-center text-sm text-gray-500">
            <p>Data fetched from Google Sheets. Auto-updates when new data is added.</p>
            {lastUpdated && (
                <p className="mt-1">Last refreshed: {lastUpdated.toLocaleString()}</p>
            )}
            <p className="mt-2 text-xs">
                Market Maestroo Private Limited | SEBI Registration No: INH000021757 | Principal Officer: Kritika Yadav
            </p>
            </div>
        </div>
        </div>
     <Footer />
    </>
  );
};

export default PortfolioResearchDashboard;